import ActionStatus from '@sunbit-dev/common/enums/ActionStatus';
import Identification, {
  IdentificationErrors,
} from '@sunbit-dev/ui/src/components/card-apply/pages/identification/Identification';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { identifyThunk } from '../actions/identificationActions';
import { getInitialDetailsThunk } from '../actions/initialDetailsActions';
import { ERROR_CODES } from '../errors/errors';
import {
  selectApplicationDocumentSolicitaionAgreement,
  selectIdentificationErrorCode,
  selectInitialDetails,
  selectLandingDetails,
} from '../selectors';
import { datadogRum } from '@sunbit-dev/common/services/datadog';
import { useMixpanel } from '../services/mixpanel/mixPanel';
import { MixpanelCardApplylEvent } from '@sunbit-dev/common/enums/cardApply/MixpanelCardApplylEvent';
import InputTypes from '@sunbit-dev/common/enums/cardApply/InputTypes';

const IdentificationContainer: React.FC = () => {
  const dispatch = useDispatch();
  const LOW_MONTHLY_INCOME_LINE = 3500;
  const HIGH_MONTHLY_INCOME_LINE = 25000;
  const { trackForMixPanel } = useMixpanel();
  const initialDetails = useSelector(selectInitialDetails);
  const landingDetails = useSelector(selectLandingDetails);
  const [isInputDirty, setIsInputDirty] = useState({ ssn: false, income: false, verifyCode: false });

  React.useEffect(() => {
    trackForMixPanel(MixpanelCardApplylEvent.IDpage_views);
    if (!initialDetails.id) {
      dispatch(getInitialDetailsThunk());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorCode = useSelector(selectIdentificationErrorCode);
  const solicitationAgreement = useSelector(selectApplicationDocumentSolicitaionAgreement);

  const submit = React.useCallback(
    (identicationRequest) => {
      trackForMixPanel(MixpanelCardApplylEvent.IDpage_submitAtp);
      dispatch(identifyThunk(identicationRequest, landingDetails.referringPartner));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onFullSSNRequiredShow = () => {
    datadogRum.addAction(`show-full-ssn-required`, {});
  };

  if (initialDetails.status === ActionStatus.Failure) {
    return null;
  }
  if (initialDetails.status !== ActionStatus.Success) {
    return null;
  }

  const values = {
    ssnLast4Digits: '',
    pinCode: '',
    dateOfBirth: '',
    monthlyIncome: 0,
    housingExpenseType: 'own',
    monthlyHousing: 0,
  };

  let error: IdentificationErrors | null = null;
  switch (errorCode) {
    case ERROR_CODES.ssnIsMandatory:
    case ERROR_CODES.fullSsnLoginRequired:
      error = IdentificationErrors.fullSsnLoginRequired;
      break;
    case ERROR_CODES.loginFailed:
      error = IdentificationErrors.detailsDontMatch;
      break;
  }

  const onChangeMiddleware =
    (onChange: (...props: any) => void, inputId: number) =>
    (...props: any) => {
      let value: any;
      let name: string;
      let applyOnChange;
      if (inputId === InputTypes.INCOME) {
        value = props[0];
        name = props[1];
        applyOnChange = () => onChange(value, name);
      } else {
        //the props is an event object
        value = props[0].target.value;
        applyOnChange = (propsArr: any) => onChange(propsArr[0]);
      }

      if (value.length === 1) {
        switch (inputId) {
          case InputTypes.SSN:
            !isInputDirty.ssn && trackForMixPanel(MixpanelCardApplylEvent.IDpage_SSN);
            setIsInputDirty((prevState) => ({ ...prevState, ssn: true }));
            break;
          case InputTypes.INCOME:
            !isInputDirty.income && trackForMixPanel(MixpanelCardApplylEvent.IDpage_Income);
            setIsInputDirty((prevState) => ({ ...prevState, income: true }));
            break;
        }
      }
      applyOnChange(props);
    };

  return (
    <Identification
      lowMonthlyIncomeLine={LOW_MONTHLY_INCOME_LINE}
      highMonthlyIncomeLine={HIGH_MONTHLY_INCOME_LINE}
      onFullSSNRequiredShow={onFullSSNRequiredShow}
      firstName={initialDetails.customerFirstName || ''}
      applicationAprMax={initialDetails.applicationAprMax || 29.99}
      applicationAprMin={initialDetails.applicationAprMin || 8.74}
      values={values}
      solicitationAgreement={solicitationAgreement}
      error={error}
      submit={submit}
      referringPartner={landingDetails.referringPartner || ''}
      onChangeMiddleware={onChangeMiddleware}
    />
  );
};

export default IdentificationContainer;
